import { useEffect, useState } from 'react';
import { Check, Minus, X } from 'lucide-react';
import { softwareRegistrationAPI as plansAPI } from '../../lib/softwareRegistrationApi';

/**
 * Kategorie-Vergleich: Salon-Tool / Medizin-Spezialist / Make It Booking.
 * Keine Konkurrenznamen, nur Kategorien. Preisangabe der Medizin-Spezialisten
 * ist über oeffentliche Preisseiten belegbar (Stand 07/2026).
 */

const ROWS = [
  { label: 'Online-Buchung', salon: 'yes', med: 'yes', mib: 'yes' },
  { label: 'Patientenakte und Anamnese', salon: 'no', med: 'yes', mib: 'yes' },
  { label: 'Gesichts- und Körperkarte', salon: 'no', med: 'partial', mib: 'yes' },
  { label: 'GOÄ-Abrechnungsvorbereitung', salon: 'no', med: 'yes', mib: 'yes' },
  { label: 'Marketing im selben Tool', salon: 'yes', med: 'no', mib: 'yes' },
  { label: 'Für medizinische Ästhetik gebaut', salon: 'no', med: 'yes', mib: 'yes' },
];

const COLS = [
  { key: 'salon', name: 'Salon-Tool', price: 'Für Salons gebaut' },
  { key: 'med', name: 'Medizin-Spezialist', price: 'ab 349 € im Monat' },
  { key: 'mib', name: 'Make It Booking', price: 'ab 49 € im Monat', highlight: true },
];

function Mark({ state, highlight }) {
  if (state === 'yes') {
    return (
      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${highlight ? 'bg-[#EC2B72]' : 'bg-[#FADCE8]'}`}>
        <Check className={`h-3.5 w-3.5 ${highlight ? 'text-white' : 'text-[#D72D6B]'}`} strokeWidth={3} />
      </span>
    );
  }
  if (state === 'partial') {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#F1EDEE]">
        <Minus className="h-3.5 w-3.5 text-[#A79BA1]" strokeWidth={3} />
      </span>
    );
  }
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#F1EDEE]">
      <X className="h-3.5 w-3.5 text-[#A79BA1]" strokeWidth={3} />
    </span>
  );
}

export default function CategorySection() {
  const [starterPrice, setStarterPrice] = useState(129);

  useEffect(() => {
    let active = true;
    plansAPI.publicCatalogue()
      .then((response) => {
        const starter = (response.data?.plans || []).find((plan) => String(plan.plan || '').toLowerCase() === 'starter');
        const price = Number(starter?.annual_net_cents) / 1200;
        if (active && Number.isFinite(price) && price > 0) setStarterPrice(price);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const columns = COLS.map((column) => (
    column.key === 'mib' ? { ...column, price: `ab ${starterPrice} € im Monat bei jährlicher Vorauszahlung` } : column
  ));

  return (
    <section id="vergleich" className="px-6 py-20 md:px-10 md:py-28 lg:px-14">
      <div className="mx-auto max-w-[1180px]" data-reveal>
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#F02672]">Der Vergleich</p>
          <h2 className="overflow-visible pb-1 text-3xl font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
            Bisher musstest du dich{' '}
            <span className="landing-gradient-text inline-block bg-gradient-to-r from-[#F05A8A] via-[#EC2B72] to-[#C83CE0] bg-clip-text pb-[0.22em] pl-[0.04em] pr-[0.16em] pt-[0.08em] italic font-light leading-[1.2] text-transparent">
              entscheiden.
            </span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-[#6F6067]">
            Entweder ein Salon-Tool mit Marketing, aber ohne Patientenakte. Oder eine Medizin-Software mit Akte,
            aber ohne Marketing und zum Vielfachen des Preises.
          </p>
        </div>

        <div className="mt-12 overflow-hidden rounded-[2rem] border border-[#2A1720]/10 bg-white shadow-[0_18px_60px_-40px_rgba(42,23,32,0.35)]">
          <div className="grid grid-cols-[1.4fr_repeat(3,1fr)] gap-2 border-b border-[#2A1720]/10 bg-[#F8EEF2] px-4 py-5 md:gap-4 md:px-8">
            <div />
            {columns.map((col) => (
              <div key={col.key} className="text-center">
                <p className={`text-sm font-semibold leading-tight md:text-base ${col.highlight ? 'text-[#D72D6B]' : 'text-[#171014]'}`}>
                  {col.name}
                </p>
                <p className="mt-1 text-[11px] leading-tight text-[#7B6A72] md:text-xs">{col.price}</p>
              </div>
            ))}
          </div>

          {ROWS.map((row, index) => (
            <div
              key={row.label}
              className={`grid grid-cols-[1.4fr_repeat(3,1fr)] items-center gap-2 px-4 py-4 md:gap-4 md:px-8 ${
                index % 2 === 1 ? 'bg-[#FFFBFC]' : 'bg-white'
              }`}
            >
              <p className="text-[13px] leading-snug text-[#43353B] md:text-[15px]">{row.label}</p>
              {columns.map((col) => (
                <div key={col.key} className="flex justify-center">
                  <Mark state={row[col.key]} highlight={col.highlight} />
                </div>
              ))}
            </div>
          ))}
        </div>

        <p className="mt-5 text-center text-xs text-[#7B6A72]">
          Kategorien statt Anbieternamen. Preisangabe der Medizin-Spezialisten nach öffentlich einsehbaren
          Preisseiten, Stand Juli 2026.
        </p>
      </div>
    </section>
  );
}
